import useAnalytics from "@/core/plugins/analytics/useAnalytics";
import useStorage from "@/core/plugins/useStorage";
import { getCurrentInstance } from "vue";
import { Router } from "vue-router";

export enum AnalyticsIntegracoesCategory {
  Panel = "integracoes_painel"
}

export enum AnalyticsIntegracoesEvent {
  SearchIntegration = "integracao_buscar",
  SaveIntegration = "integracao_salvar",
  EditIntegration = "integracao_editar",
  DeleteIntegration = "integracao_excluir",
  ToggleIntegrationStatus = "integracao_alterar_status",
  SearchAgain = "integracao_buscar_novamente",
  OpenHistory = "integracao_abrir_historico"
}

export enum AnalyticsIntegracoesResult {
  Success = "sucesso",
  Error = "erro"
}

export enum AnalyticsIntegracoesHistoryOrigin {
  MoreOptions = "mais_opcoes",
  SearchAgain = "buscar_novamente"
}

export type TAnalyticsIntegracoesSearchAgainDate = {
  startDate: string | null;
  endDate: string | null;
};

const formatDateOnly = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const formatAnalyticsIntegracoesDate = (value: unknown) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : formatDateOnly(value);
  }

  const stringValue = String(value).trim();
  if (!stringValue) return null;

  const dateOnlyMatch = /^(\d{4}-\d{2}-\d{2})/.exec(stringValue);
  if (dateOnlyMatch) return dateOnlyMatch[1];

  const parsedDate = new Date(stringValue);

  return Number.isNaN(parsedDate.getTime()) ? null : formatDateOnly(parsedDate);
};

export default function useAnalyticsIntegracoes() {
  const storage = useStorage();
  const router = getCurrentInstance()?.appContext.config.globalProperties
    .$router as Router | undefined;

  const { sendEvent } = useAnalytics(storage, router);

  const sendPanelEvent = (
    eventName: AnalyticsIntegracoesEvent,
    props: Record<string, string | number | boolean | null> = {}
  ) => sendEvent(AnalyticsIntegracoesCategory.Panel, eventName, props);

  const sendSearchIntegration = (integrationName: string) =>
    sendPanelEvent(AnalyticsIntegracoesEvent.SearchIntegration, {
      nome_integracao: integrationName
    });

  const sendSaveIntegration = (
    integrationName: string,
    result: AnalyticsIntegracoesResult
  ) =>
    sendPanelEvent(AnalyticsIntegracoesEvent.SaveIntegration, {
      nome_integracao: integrationName,
      resultado: result
    });

  const sendEditIntegration = (
    integrationName: string,
    result: AnalyticsIntegracoesResult
  ) =>
    sendPanelEvent(AnalyticsIntegracoesEvent.EditIntegration, {
      nome_integracao: integrationName,
      resultado: result
    });

  const sendDeleteIntegration = (integrationName: string) =>
    sendPanelEvent(AnalyticsIntegracoesEvent.DeleteIntegration, {
      nome_integracao: integrationName
    });

  const sendToggleIntegrationStatus = (
    integrationName: string,
    active: boolean
  ) =>
    sendPanelEvent(AnalyticsIntegracoesEvent.ToggleIntegrationStatus, {
      nome_integracao: integrationName,
      ativo: active
    });

  const sendSearchAgain = (
    integrationName: string,
    result: AnalyticsIntegracoesResult,
    date: TAnalyticsIntegracoesSearchAgainDate
  ) =>
    sendPanelEvent(AnalyticsIntegracoesEvent.SearchAgain, {
      nome_integracao: integrationName,
      resultado: result,
      data_inicio: date.startDate,
      data_fim: date.endDate
    });

  const sendOpenHistory = (
    integrationName: string,
    origin: AnalyticsIntegracoesHistoryOrigin
  ) =>
    sendPanelEvent(AnalyticsIntegracoesEvent.OpenHistory, {
      nome_integracao: integrationName,
      origem: origin
    });

  return {
    sendSearchIntegration,
    sendSaveIntegration,
    sendEditIntegration,
    sendDeleteIntegration,
    sendToggleIntegrationStatus,
    sendSearchAgain,
    sendOpenHistory
  };
}
